<template>
  <div class="d-flex flex-column h-100 pb-4 mx-auto justify-content-between" style="max-width: 600px;"
    :style="{ maxHeight: inventoryMode === 'step' ? '800px' : 'none' }">

    <!-- Header -->
    <div class="header">
      <div class="d-flex" v-if="!currentArea || isOnlyOtherArea">
        <h1 class="m-0">
          <span class="d-none d-md-inline">Inventory - </span>
          {{this.stockDays.find((d) => d.id == inventory.day)?.dateHeader}}
        </h1>
        <div class="ms-auto">
          <Button icon="pi pi-pencil" title="Edit" class="p-button-text p-button-secondary"
            @click="$refs.form.show(inventory)" />
          <Button icon="pi pi-trash" title="Delete" class="p-button-text p-button-danger me-3" @click="destroy"
            :loading="loading" />
          <Button icon="pi pi-save" title="Save" class="p-button-success" @click="save" :loading="loading" />
        </div>
      </div>
      <div v-if="currentArea" class="d-flex justify-content-between align-items-center mt-0 pt-3 mb-3">
        <Button v-if="!isOnlyOtherArea" icon="pi pi-arrow-left" title="Back" class="p-button-secondary"
          @click="currentArea = null" />
        <h3 v-if="!isOnlyOtherArea" class="d-flex align-items-center m-0">
          <i class="me-2 fs-5 pi pi-map-marker"></i>
          {{ currentArea.name }}
        </h3>
        <SelectButton v-model="inventoryMode"
          :options="[{ id: 'step', label: '1-by-1' }, { id: 'list', label: 'List' }]" aria-labelledby="inventoryMode"
          optionLabel="label" optionValue="id" />
      </div>
    </div>

    <!-- Choose Area -->
    <template v-if="!currentArea">
      <div v-if="isInventoryLoading" class="content d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
      <div v-else-if="areas.length == 0">
        <h3>No products available. Please check your configuration</h3>
      </div>
      <div v-else class="content">
        <h3 class="text-primary">
          <span v-if="allAreasCompleted">Inventory done !</span>
          <span v-else-if="noneAreaCompleted">Choose the Area to Start</span>
          <span v-else>Choose the Next Area</span>
        </h3>
        <div v-for="area in areas" :key="area.id" @click="startArea(area)"
          class="storage-area-card border mb-3 rounded-3 p-3" :class="{ completed: allProductsForAreaCompleted(area) }">
          <i v-if="isAreaCompleted(area)" class="pi-check-circle pi me-2"></i>
          <i v-else class="pi-circle pi me-2"></i>
          {{ area.name }}
          <span class="float-end">
            {{ completedProductsForArea(area).length }} / {{ productsForArea(area).length }}
          </span>
        </div>
      </div>
      <div class="footer">
        <Button label="Save" class="w-100 p-button-success" @click="save" :loading="loading" />
      </div>
    </template>

    <!-- Fill Area Stocks - Step Mode -->
    <template
      v-if="currentArea && inventoryMode === 'step' && (inventory.values[currentProduct.id] || {})[currentArea?.id]">
      <div class="content">
        <h3 class="mb-4">{{ currentProduct.name }}</h3>
        <!-- Unit -->
        <div v-if="currentProduct.packaging_convert_to_piece && currentProduct.unit != 'piece'"
          class="d-flex align-items-center mb-4 justify-content-center">
          <RadioButton v-model="inventory.values[currentProduct.id][currentArea.id].unit" id="unit-default" name="unit"
            :value="currentProduct.unit" />
          <label for="unit-default" class="ms-2 me-4">{{ currentProduct.unit }}</label>
          <RadioButton v-model="inventory.values[currentProduct.id][currentArea.id].unit" id="unit-piece" name="unit"
            value="piece" />
          <label for="unit-piece" class="ms-2">
            piece ({{ currentProduct.packaging_conditioning }}{{ currentProduct.unit }})
            <Tag :value="currentProduct.packaging_reference" class="ms-2 p-tag-secondary" />
          </label>
        </div>
        <!-- Stock -->
        <div class="p-inputgroup">
          <InputNumber v-model="inventory.values[currentProduct.id][currentArea.id].value"
            @input="currentProductEdited = true" :maxFractionDigits="5" placeholder="Stock" ref="stockInput"
            @keyup.enter="finishProduct" inputmode="numeric" />
          <span class="p-inputgroup-addon" style="width: 5rem;">
            {{ inventory.values[currentProduct.id][currentArea.id].unit }}
          </span>
        </div>
        <!-- Theoritical Stock -->
        <div class="fst-italic mt-4" v-if="!currentProduct.fixed_stock">
          <template v-if="currentProduct.storage_area_ids.length <= 1">Theoritical Stock</template>
          <template v-else>Total Theoritical Stock ({{currentProduct.storage_area_ids.map(id =>
            $root.getCategory(id).name).join(' + ')}})</template>
          = {{ currentTheoriticalStock }}
        </div>
      </div>

      <!-- Navigation -->
      <div class="footer d-flex align-items-center justify-content-between">
        <Button label="Prev" icon="pi pi-chevron-left" class="p-button-secondary"
          @click="productIndex = Math.max(productIndex - 1, 0)" :disabled="productIndex == 0" />
        <div>{{ productIndex + 1 }} / {{ currentProducts.length }}</div>
        <Button v-if="productIndex == currentProducts.length - 1" label="Finish" @click="finishProduct" />
        <Button v-else label="Next" icon="pi pi-chevron-right" @click="finishProduct" />
      </div>
    </template>

    <!-- Fill Area Stocks - List Mode -->
    <template v-if="currentArea && inventoryMode === 'list'">
      <div class="content list-content h-100">
        <template v-for="(product, idx) in currentProducts" :key="product.id">
          <div v-if="idx === 0 || product.category_id !== currentProducts[idx - 1].category_id" class="category-header">
            {{ $root.getCategory(product.category_id).name || 'Uncategorized' }}
          </div>
          <div class="list-row" v-if="(inventory.values[product.id] || {})[currentArea.id]">
            <span class="product-name">{{ product.name }}</span>
            <div class="d-flex align-items-center gap-2">
              <Dropdown v-if="product.packaging_convert_to_piece && product.unit !== 'piece'"
                v-model="inventory.values[product.id][currentArea.id].unit" :options="[
                  { label: product.unit, value: product.unit },
                  { label: `piece (${product.packaging_conditioning}${product.unit}) - ${product.packaging_reference}`, value: 'piece' }
                ]" optionLabel="label" optionValue="value" class="unit-select" />
              <div class="p-inputgroup list-input-group">
                <InputNumber v-model="inventory.values[product.id][currentArea.id].value" :maxFractionDigits="5"
                  placeholder="0" inputmode="numeric" />
                <span class="p-inputgroup-addon">
                  {{ inventory.values[product.id][currentArea.id].unit }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="footer mt-4">
        <Button label="Finish" class="w-100" @click="finishCurrentArea" />
      </div>
    </template>
  </div>

  <InventoryNewDialog ref="form" @inventory-updated="onInventoryUpdated($event)" />
</template>

<script>
import InputNumber from 'primevue/inputnumber'
import Tag from 'primevue/tag'
import RadioButton from 'primevue/radiobutton'
import StockMixin from '@/services/stocks-mixin'
import { normalizeQuantityUnit } from '@/services/units'
import { debounce } from '@/services/debounce'
import {
  readDraft,
  writeDraft,
  clearDraft,
  isDraftDifferent,
  serializeDraftFields,
} from '@/services/inventory-draft-storage'
import InventoryNewDialog from './InventoryNewDialog.vue'
import SelectButton from 'primevue/selectbutton'
import Spinner from '@/components/Spinner.vue'

export default {
  inject: ['sessionInventories', 'setUnsavedChanges'],
  mixins: [StockMixin],
  components: {
    InputNumber, RadioButton, Tag, InventoryNewDialog, SelectButton, Spinner,
  },
  data() {
    return {
      inventory: {
        storage_area_ids: [],
        product_category_ids: [],
        supplier_ids: [],
        completed_storage_areas_ids: [],
        values: {},
      },
      otherArea: { name: 'Other Products', id: 'other' },
      currentArea: undefined,
      productIndex: 0,
      inventoryMode: localStorage.getItem('inventoryMode') || 'step',
      currentProductEdited: false,
      loading: false,
      dirty: false,
      serverSnapshot: null,
      draftReady: false,
      draftRecovered: false,
    }
  },
  beforeMount() {
    this.inventory = this.sessionInventories.find((inv) => inv.id == this.$route.params.inventory_id)
    this.normalizeInventoryUnits()
    this.persistDraftDebounced = debounce(() => {
      this.persistDraft()
    }, 400)
  },
  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty) {
      next()
      return
    }
    this.$confirm.require({
      message: 'You have unsaved inventory changes. Leave without saving?',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Quit without saving',
      rejectLabel: 'Cancel',
      accept: () => next(),
      reject: () => next(false),
    })
  },
  computed: {
    areas() {
      let result = Object.values(this.$root.categories)
        .filter((c) => c.type === 'StorageArea')
        .sort((a, b) => a.name.localeCompare(b.name))

      let areasIdsInUse = this.products.reduce((acc, product) => acc.concat(product.storage_area_ids), [])
      areasIdsInUse = Array.from(new Set(areasIdsInUse))
      if (areasIdsInUse.lentgh > 0) result = result.filter((c) => areasIdsInUse.includes(c.id))

      if (this.inventory.storage_area_ids.length > 0) {
        result = result.filter((c) => this.inventory.storage_area_ids.includes(c.id))
      } else {
        result.push(this.otherArea)
        result = result.filter((area) => this.productsForArea(area).length > 0)
      }

      return result
    },
    isAreaCompleteds() {
      return this.areas.filter((area) => this.inventory.completed_storage_areas_ids.includes(area.id.toString()))
    },
    products() {
      let result = this.sessionProducts
        .map((productId) => this.$root.getProduct(productId))
        .filter((product) => product.id)
      if (this.inventory.supplier_ids.length > 0) {
        result = result.filter((product) => this.inventory.supplier_ids.includes(product.supplier_id))
      }
      if (this.inventory.product_category_ids.length > 0) {
        result = result.filter((product) => this.inventory.product_category_ids.includes(product.category_id))
      }
      result = result.sort((a, b) => {
        // sort first by category, then by name
        const aValue = this.$root.getCategory(a.category_id).name || 'xxxx'
        const bValue = this.$root.getCategory(b.category_id).name || 'xxxx'
        if ((!aValue || aValue === bValue) && a.name && b.name) {
          return a.name.localeCompare(b.name)
        }
        return aValue.localeCompare(bValue)
      })
      return result
    },
    // Products related to currentArea
    currentProducts() {
      return this.currentArea ? this.productsForArea(this.currentArea) : []
    },
    currentProduct() {
      return this.currentProducts.at(this.productIndex) || {}
    },
    currentCategory() {
      return this.$root.getCategory(this.currentProduct.category_id).name
    },
    currentUnit() {
      return this.inventory.values[this.currentProduct.id][this.currentArea.id].unit
    },
    currentValue() {
      return this.inventory.values[this.currentProduct.id][this.currentArea.id].value
    },
    currentTheoriticalStock() {
      const theoricValue = Math.max(0, this.theoricStockFor(this.currentProduct.id, this.inventory.day))
      const theoricLabel = `${theoricValue.round()} ${this.currentProduct.unit}`
      if (this.currentProduct.packaging_convert_to_piece && this.currentUnit === 'piece') {
        return `${(theoricValue / this.currentProduct.packaging_conditioning).round()} pieces (${theoricLabel})`
      }
      return theoricLabel
    },
    allAreasCompleted() {
      return this.areas.length === this.isAreaCompleteds.length
    },
    noneAreaCompleted() {
      return this.isAreaCompleteds.length === 0
    },
    isOnlyOtherArea() {
      return this.areas.length === 1 && this.areas[0].id === 'other'
    },
    isInventoryLoading() {
      return this.stocksLoading || !this.stocksReady
    },
  },
  methods: {
    normalizeInventoryUnits() {
      Object.entries(this.inventory.values || {}).forEach(([productId, areaValues]) => {
        const product = this.$root.getProduct(productId) || {}
        Object.values(areaValues || {}).forEach((areaValue) => {
          areaValue.unit = normalizeQuantityUnit(areaValue.unit, product)
        })
      })
    },
    startArea(area) {
      this.currentArea = area
      this.productIndex = 0
      this.focusStockInput()
    },
    finishProduct() {
      const newStock = this.reCalculateStockFor(this.currentProduct.id, this.inventory.day)

      // If the product have only one storage area, then we got the whole stock for
      // this product, and we can compare it with theotical stock
      if (this.$root.userData.inventory_stock_diff_alert
        && this.currentProductEdited
        && this.currentProduct.storage_area_ids.length <= 1) {
        const newValue = newStock.values[this.inventory.day]
        const theoricVal = Math.max(0, newValue.theoric)
        // Special case: if theoric is -5 but real stock is 0, then stockDiff will be big,
        // although it does not make sens to display the warning in such case cause
        // user will see theotic = 0, real = 0
        if (newValue.stockDiff > this.$root.userData.inventory_stock_diff_threshold_percent / 100
          && this.currentValue !== null
          && (theoricVal !== 0 || newValue.real !== 0)) {
          let theoric = `${theoricVal.round()} ${newStock.product_unit}`
          let real = `${newValue.real} ${newStock.product_unit}`
          if (this.currentProduct.packaging_convert_to_piece && this.currentUnit === 'piece') {
            theoric = `${(theoricVal / this.currentProduct.packaging_conditioning).round()} pieces (${theoric})`
            real = `${this.currentValue} (${real})`
          }
          this.$confirm.require({
            header: 'The stock seems wrong',
            message: `Theoretical stock is ${theoric}
              but you entered ${real}

              Are you sure this is the correct value?
            `,
            rejectLabel: 'Cancel',
            acceptLabel: "Yes I'm sure",
            accept: async () => {
              this.goToNextProductOrArea()
            },
          })
          return
        }
      }

      this.goToNextProductOrArea()
    },
    goToNextProductOrArea() {
      if (this.productIndex === this.currentProducts.length - 1) this.finishCurrentArea()
      else this.productIndex += 1
    },
    finishCurrentArea() {
      this.inventory.completed_storage_areas_ids.push(this.currentArea.id.toString())
      this.currentArea = null
    },
    isAreaCompleted(area) {
      return this.inventory.completed_storage_areas_ids.includes(area.id.toString())
    },
    allProductsForAreaCompleted(area) {
      return this.completedProductsForArea(area).length === this.productsForArea(area).length
    },
    productsForArea(area) {
      return this.products.filter((product) => {
        if (area.id === 'other') return product.storage_area_ids.length === 0
        return product.storage_area_ids.includes(area.id)
      })
    },
    completedProductsForArea(area) {
      return this.productsForArea(area).filter((product) => (this.inventory.values[product.id] || {})[area.id]?.value != null)
    },
    focusStockInput() {
      this.$nextTick(() => {
        if (this.$refs.stockInput) this.$refs.stockInput.$refs.input.$el.focus()
      })
    },
    onBeforeUnload(event) {
      if (!this.dirty) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    },
    ensureProductValueSlots() {
      if (!this.products || this.products.length === 0) return
      this.products.forEach((product) => {
        product.storage_area_ids ||= [] // in case the product do not have storage set yet
        const areasIds = product.storage_area_ids.length > 0 ? product.storage_area_ids : ['other']
        this.inventory.values[product.id] ||= {}
        areasIds.forEach((areaId) => {
          this.inventory.values[product.id][areaId] ||= { unit: product.unit }
          this.inventory.values[product.id][areaId].unit = normalizeQuantityUnit(
            this.inventory.values[product.id][areaId].unit,
            product
          )
        })
      })
    },
    captureServerSnapshot() {
      this.serverSnapshot = serializeDraftFields(this.inventory)
      this.dirty = false
      this.setUnsavedChanges(false)
      this.draftReady = true
    },
    updateDirtyState() {
      if (!this.draftReady || !this.serverSnapshot) return
      this.dirty = serializeDraftFields(this.inventory) !== this.serverSnapshot
      this.setUnsavedChanges(this.dirty)
      if (this.dirty) this.persistDraftDebounced()
    },
    persistDraft() {
      if (!this.dirty || !this.inventory?.id) return
      writeDraft({
        inventoryId: this.inventory.id,
        sessionId: this.inventory.session_id || this.$route.params.id,
        values: this.inventory.values,
        completed_storage_areas_ids: this.inventory.completed_storage_areas_ids,
      })
    },
    applyDraft(draft) {
      this.inventory.values = JSON.parse(JSON.stringify(draft.values || {}))
      this.inventory.completed_storage_areas_ids = [
        ...(draft.completed_storage_areas_ids || []),
      ]
      this.normalizeInventoryUnits()
      this.ensureProductValueSlots()
      this.dirty = true
      this.setUnsavedChanges(true)
      this.persistDraft()
    },
    maybeRecoverDraft() {
      if (this.draftRecovered || !this.inventory?.id) return
      this.draftRecovered = true
      const draft = readDraft(this.inventory.id)
      if (!draft || !isDraftDifferent(draft, this.inventory)) return
      this.applyDraft(draft)
    },
    async save() {
      const result = await this.dbUpdate('inventories', this.inventory)
      if (!result) return
      clearDraft(this.inventory.id)
      this.captureServerSnapshot()
    },
    destroy() {
      this.$confirm.require({
        message: 'Are you sure you want to delete this inventory ?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          const inventoryId = this.inventory.id
          const deleted = await this.dbDestroy('inventories', this.inventory)
          if (!deleted) return
          clearDraft(inventoryId)
          this.dirty = false
          this.setUnsavedChanges(false)
          if (this.$root.isPublicMode) {
            this.$router.push({ name: 'inventories_public', params: { id: this.$route.params.id } })
          } else {
            this.$router.push({ name: 'session_overview', params: { id: this.$route.params.id } })
          }
        },
      })
    },
    onInventoryUpdated(data) {
      this.inventory = data
      this.normalizeInventoryUnits()
      this.ensureProductValueSlots()
      this.currentArea = null
      clearDraft(this.inventory.id)
      this.captureServerSnapshot()
    },
  },
  watch: {
    // initialize values for each product
    products: {
      immediate: true,
      handler() {
        if (!this.products || this.products.length === 0) return
        this.ensureProductValueSlots()
        if (!this.draftReady) {
          this.captureServerSnapshot()
          this.$nextTick(() => this.maybeRecoverDraft())
        }
      },
    },
    'inventory.values': {
      deep: true,
      handler() {
        this.updateDirtyState()
      },
    },
    'inventory.completed_storage_areas_ids': {
      deep: true,
      handler() {
        this.updateDirtyState()
      },
    },
    inventoryMode(val) {
      localStorage.setItem('inventoryMode', val)
    },
    async currentProduct() {
      this.currentProductEdited = false
      this.focusStockInput()
    },
    areas() {
      // Auto-navigate if there's only one area and it's the otherArea
      if (this.isOnlyOtherArea && !this.currentArea && !this.isAreaCompleted(this.areas[0])) {
        this.startArea(this.areas[0])
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.content {
  text-align: center;
  padding-bottom: 20%;
}

.storage-area-card {
  background-color: white;
  text-align: left;

  &:hover {
    background-color: var(--primary-color);
    color: white;
    // background-color: var(--bluegray-100);
    // font-weight: bold;
    cursor: pointer;
  }

  &.completed {
    opacity: .5;
  }
}

.list-content {
  text-align: left;
  padding-bottom: 0;
  overflow-y: auto;

  .list-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--surface-border, #dee2e6);
    gap: 0.5rem;
  }

  .category-header {
    font-weight: 600;
    text-transform: uppercase;
    color: var(--primary-color);
    padding: 1.5rem 0 .75rem;
    margin-bottom: .5rem;
    border-bottom: 2px solid var(--primary-color);
  }

  .product-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .list-input-group {
    width: 10rem;
  }
}



.p-tag-secondary {
  color: inherit;
  background-color: var(--bluegray-100);
}

.p-confirm-dialog-message {
  margin-left: 0;
  line-height: 1.8;
}
</style>
